import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Slider,
  Paper
} from '@mui/material';
import { ZoomIn, ZoomOut, FileDownload, Print } from '@mui/icons-material';
import ChartGrid from './ChartGrid';
import InstructionsPanel from './InstructionsPanel';
import KeyPanel from './KeyPanel';
import { createPrintHandler } from './printUtils';

const ChartViewer = ({ 
  knitdata, 
  colordata, 
  colors, 
  icons, 
  verticalLines = [],
  title = "Knitting Chart",
  showTitle = true,
  defaultCellSize = 16,
  onExport 
}) => {
  const [cellSize, setCellSize] = useState(defaultCellSize);
  const [showInstructions, setShowInstructions] = useState(true);
  const [showKey, setShowKey] = useState(true);
  const [showVerticalLines, setShowVerticalLines] = useState(true);
  const [startWithRS, setStartWithRS] = useState(true);

  // Convert knitdata to text instructions
  const knitdataToInstructions = () => {
    if (!knitdata || !Array.isArray(knitdata) || knitdata.length === 0) return '';
    
    return knitdata.map((row, i) => {
      const rowNum = i + 1;
      if (!Array.isArray(row) || row.length === 0 || row.every(stitch => !stitch)) {
        return `Row ${rowNum}: (empty)`;
      }
      
      const stitches = [];
      let currentStitch = row[0] || 'k';
      let count = 1;

      for (let j = 1; j < row.length; j++) {
        const stitch = row[j] || 'k';
        if (stitch === currentStitch) {
          count++;
        } else {
          stitches.push(count > 1 ? `${currentStitch}${count}` : currentStitch);
          currentStitch = stitch;
          count = 1;
        }
      }
      stitches.push(count > 1 ? `${currentStitch}${count}` : currentStitch);

      return `Row ${rowNum}: ${stitches.join(', ')}`;
    }).join('\n');
  };

  // Export chart data as JSON file
  const handleExportChart = () => {
    if (onExport) {
      onExport({
        knitdata,
        verticalLines,
        colordata,
        colors,
        icons
      });
    } else {
      const data = JSON.stringify({
        knitdata,
        verticalLines: showVerticalLines ? verticalLines : [],
        colordata,
        colors,
        icons,
        title
      }, null, 2);
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}-chart.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  // Print handler - creates a print-optimized view
  const handlePrint = createPrintHandler(title);

  // Zoom handlers
  const handleZoomIn = () => {
    setCellSize(Math.min(cellSize + 4, 48));
  };

  const handleZoomOut = () => {
    setCellSize(Math.max(cellSize - 4, 12));
  };

  // Handle cell size slider change
  const handleCellSizeChange = (_, newValue) => {
    setCellSize(newValue);
  };

  if (!knitdata || !Array.isArray(knitdata) || knitdata.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No chart data available
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please provide valid chart data to display.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 600 }}>
      {/* Header with title and controls */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          {showTitle && (
            <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold' }}>
              {title}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Zoom Out">
              <IconButton onClick={handleZoomOut} disabled={cellSize <= 12} size="small">
                <ZoomOut fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Zoom In">
              <IconButton onClick={handleZoomIn} disabled={cellSize >= 48} size="small">
                <ZoomIn fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export Chart">
              <IconButton onClick={handleExportChart} size="small">
                <FileDownload fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Print Chart">
              <IconButton onClick={handlePrint} size="small">
                <Print fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* View Controls */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ minWidth: 80 }}>
              Cell Size:
            </Typography>
            <Slider
              value={cellSize}
              onChange={handleCellSizeChange}
              min={12}
              max={48}
              step={2}
              sx={{ width: 100 }}
              size="small"
            />
            <Typography variant="body2" sx={{ minWidth: 30 }}>
              {cellSize}px
            </Typography>
          </Box>
          
          <FormControlLabel
            control={
              <Switch 
                checked={startWithRS} 
                onChange={(e) => setStartWithRS(e.target.checked)}
                size="small"
              />
            }
            label="Start with RS"
          />

          <FormControlLabel
            control={
              <Switch 
                checked={showVerticalLines} 
                onChange={(e) => setShowVerticalLines(e.target.checked)}
                size="small"
              />
            }
            label="Vertical Lines"
          />

          <FormControlLabel
            control={
              <Switch 
                checked={showKey} 
                onChange={(e) => setShowKey(e.target.checked)}
                size="small"
              />
            }
            label="Stitch Key"
          />

          <FormControlLabel
            control={
              <Switch 
                checked={showInstructions} 
                onChange={(e) => setShowInstructions(e.target.checked)}
                size="small"
              />
            }
            label="Instructions"
          />
        </Box>
      </Paper>

      {/* Chart Content */}
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, flex: 1 }}>
        {/* Main Chart */}
        <Box sx={{ flex: 'none' }} data-chart-print-content>
          <ChartGrid 
            knitdata={knitdata}
            colordata={colordata}
            colors={colors}
            icons={icons}
            verticalLines={showVerticalLines ? verticalLines : []}
            cellSize={cellSize}
            startWithRS={startWithRS}
            // No click handlers - this is view-only
            onStitchClick={undefined}
            onClearCell={undefined}
            onToggleVerticalLine={undefined}
            deleteMode={false}
          />
        </Box>

        {/* Stitch Key Panel - positioned right next to chart */}
        {showKey && (
          <Box sx={{ flex: 'none', minWidth: 200 }} data-key-print-content>
            <KeyPanel grid={knitdata} cellSize={cellSize} />
          </Box>
        )}
      </Box>

      {/* Instructions Panel */}
      {showInstructions && (
        <Box sx={{ mt: 2 }}>
          <InstructionsPanel instructions={knitdataToInstructions()} />
        </Box>
      )}
    </Box>
  );
};

export default ChartViewer;
